import { api } from "../client";

export interface Company {
  id: string;
  name: string;
  role: "owner" | "admin" | "operator" | "viewer";
  /**
   * Default model id used when a new agent host row is created for this
   * company (e.g., "claude-opus-4-7"). Per-role overrides live on
   * `cloud_agent_hosts.model` — see `api/queries/agents.ts`.
   */
  ceo_model: string;
  device_count: number;
  created_at: number;
}

export const companiesApi = {
  list: () => api<{ companies: Company[] }>("/v1/companies"),
  get: (id: string) => api<Company>(`/v1/companies/${id}`),
  create: (input: { name: string; ceo_model?: string }) =>
    api<Company>("/v1/companies", { method: "POST", body: input }),
  rename: (id: string, name: string) =>
    api<Company>(`/v1/companies/${id}`, { method: "PATCH", body: { name } }),
  remove: (id: string) =>
    api<{ ok: true }>(`/v1/companies/${id}`, { method: "DELETE" }),
  leave: (id: string) =>
    api<{ ok: true }>(`/v1/companies/${id}/leave`, { method: "POST" }),
};

export interface AuditEvent {
  id: string;
  company_id: string;
  actor_id: string;
  actor_email: string;
  /** Hub started sending this 2026-05-10. Falls back to email for older rows. */
  actor_display_name?: string;
  action: string;
  target_type: string;
  target_id: string;
  at: number;
  diff?: unknown;
}

export const auditApi = {
  list: (
    companyId: string,
    opts: {
      cursor?: string;
      limit?: number;
      action?: string;
      actor?: string;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.cursor) params.set("cursor", opts.cursor);
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.action) params.set("action", opts.action);
    if (opts.actor) params.set("actor", opts.actor);
    const qs = params.toString();
    return api<{ events: AuditEvent[]; next_cursor: string | null }>(
      `/v1/companies/${companyId}/audit${qs ? `?${qs}` : ""}`,
    );
  },
};

export interface CompanyMember {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  joined_at: number;
  last_active_at: number | null;
  events_today: number;
}

export interface CompanyStats {
  work_orders_by_status: Array<{ status: string; n: number }>;
  steps_by_device: Array<{ device_id: string; device_name: string; n: number }>;
  agent_turns_week: Array<{ role: string; n: number }>;
  agent_turns_by_day: Array<{ day: number; ceo: number; ops: number }>;
  devices_active: number;
  devices_online: number;
}

export const dashboardApi = {
  members: (companyId: string) =>
    api<{ members: CompanyMember[] }>(`/v1/companies/${companyId}/members`),
  stats: (companyId: string) =>
    api<CompanyStats>(`/v1/companies/${companyId}/stats`),
};
